import React, { useState, useCallback, useEffect } from "react";
import { useTranslation } from "components/translations/useTranslation";
import Comment from "components/input/Comment.component";
import OKCancelButtons from "components/modals/OKCancelButtons";
import { MODAL_CONTENT_TAB_INDEX } from "utils/constants";
import UserSearch from "components/input/UserSearch.component";
import { useDispatch } from "react-redux";
import { ReassignProps, ReassignActionData } from "types/modals";
import { FormField } from "components/common/FormField.component";
import { reassignTasks, fetchDraftComment } from "components/modals/modal.actions";
import { Notification, NotificationType } from "components/common/Notification.component";

interface RecipientOption {
    value: string;
    label: string;
}

const Reassign = ({ more, closeModal, loopFocus, errorCallback }: ReassignProps): React.ReactElement => {
    const { translate } = useTranslation();
    const dispatch = useDispatch();

    const [recipient, setRecipient] = useState<RecipientOption | null>(null);
    const [comment, setComment] = useState("");
    const [actionRunning, setActionRunning] = useState(false);
    const [displayError, setDisplayError] = useState<Record<string, any> | null>(null);
    const [refresh, setRefresh] = useState(false);

    useEffect(() => {
        dispatch(fetchDraftComment(true, more.tasks, setComment));
    }, []); // eslint-disable-line react-hooks/exhaustive-deps -- intentionally run only on mount

    const handleReassignSuccess = useCallback(() => {
        closeModal();
    }, [closeModal]);

    const handleReassignError = useCallback((error: Record<string, any>) => {
        setDisplayError(error.error ?? error);
        setActionRunning(false);
    }, [errorCallback]);

    const handleReassign = useCallback(() => {
        if (!comment || !recipient?.value) return;

        setActionRunning(true);
        const keys = more.tasks.map((task) => task.key);

        const data: ReassignActionData = {
            keys,
            comment,
            recipient: recipient.value,
        };

        dispatch(reassignTasks(data, handleReassignSuccess, handleReassignError));
    }, [comment, recipient, more, dispatch, handleReassignSuccess, handleReassignError]);

    const handleRecipientChange = useCallback((value: RecipientOption | null) => {
        setRecipient(value);
        setRefresh(false);
        setActionRunning(false);
        setDisplayError(null);
    }, []);

    const handleCommentChange = useCallback((value: string) => {
        setComment(value);
        setRefresh(false);
        setDisplayError(null);
    }, []);

    const okEnabled = !!(comment && recipient && !actionRunning);
    const taskIds = more.tasks.map((task) => task.key);

    return (
        <>
            <div className="ga-modal__content">
                <FormField
                    id="recipient"
                    label={translate("popUp.forward.recipient")}
                    labelState={`(${translate("popUp.required")})`}
                >
                    <UserSearch
                        tabindex={MODAL_CONTENT_TAB_INDEX}
                        taskId={taskIds as any}
                        translate={translate as any}
                        refresh={refresh}
                        propagateValue={handleRecipientChange}
                        placeholder={translate("userSearch.placeholder")}
                        value={recipient}
                        includeSameUser={true}
                        releaseFocus={() => setRefresh(false)}
                        menuPosition="fixed"
                        menuPortalTarget={document.body}
                    />
                </FormField>
                {displayError && (
                    <div className="my-16">
                        <Notification
                            type={NotificationType.ERROR}
                            title={displayError.errorTitle}
                            description={displayError.errorMessages}
                        />
                    </div>
                )}
                <div className="mt-3">
                    <Comment
                        id="comment"
                        inputHint={translate("popUp.forward.commentHint")}
                        translate={translate}
                        tabIndex={MODAL_CONTENT_TAB_INDEX + 1}
                        propagateValue={handleCommentChange}
                        value={comment}
                        label={translate("popUp.forward.commentLabel")}
                        labelState={`(${translate("popUp.required")})`}
                    />
                </div>
            </div>
            <OKCancelButtons
                tabIndex={MODAL_CONTENT_TAB_INDEX + 2}
                okEnabled={okEnabled}
                okCallback={handleReassign}
                okText={translate("popUp.ok")}
                cancelEnabled={true}
                cancelCallback={closeModal}
                cancelText={translate("popUp.cancel")}
                loopFocus={loopFocus}
            />
        </>
    );
};

export default Reassign;
